
import React, {
    useState
}
from "react";

import useHomeIntelligenceData
from "../../hooks/useHomeIntelligenceData";

import HomeIntelligenceChart
from "./HomeIntelligenceChart";

import "./HomeIntelligenceCard.css";


//========================================================
// Executive Intelligence Card
//
// Reusable card for all intelligence subjects.
//
// Uses:
//   - useHomeIntelligenceData
//   - HomeIntelligenceChart (amCharts v4)
//
// Chart selection is controlled by the charcoal
// CHART button on the left.
//
// No Font Awesome dependencies.
// No footer.
//========================================================

export default function HomeIntelligenceCard({
    subject,
    delay = 0
})
{

    //========================================================
    // Chart Configuration
    //========================================================

    const chartTypes =
        subject.chartTypes || [];

    const initialChart =
        subject.defaultChart ||
        chartTypes[0] ||
        "Total";


    //========================================================
    // State
    //========================================================

    const [selectorOpen, setSelectorOpen] =
        useState(false);

    const [chartType, setChartType] =
        useState(initialChart);


    //========================================================
    // Intelligence Data
    //========================================================

    const {
        data,
        loading,
        error,
        isDemo
    } = useHomeIntelligenceData(
        subject.id,
        chartType
    );


    //========================================================
    // Toggle Chart Selector
    //========================================================

    const toggleSelector =
        function()
        {
            setSelectorOpen(
                function(previous)
                {
                    return !previous;
                }
            );
        };


    //========================================================
    // Select Chart Type
    //========================================================

    const selectChartType =
        function(type)
        {
            setChartType(type);

            setSelectorOpen(false);
        };


    //========================================================
    // Render
    //========================================================

    return (

        <div
            className="home-intelligence-card"

            style={{
                "--intelligence-color":
                    subject.color || "#2686cf",

                "--intelligence-delay":
                    `${delay}ms`
            }}
        >

            {/* ==========================================
                LEFT CONTROL RAIL
            ========================================== */}

            <div className="home-intelligence-card-rail">

                {/* ======================================
                    CHART SELECTOR BUTTON
                ====================================== */}

                <button
                    type="button"

                    className={
                        "home-intelligence-card-icon"
                        +
                        (
                            selectorOpen
                                ?
                                " active"
                                :
                                ""
                        )
                    }

                    onClick={toggleSelector}

                    title="Change chart type"

                    aria-label={
                        "Change chart type for "
                        +
                        subject.title
                    }

                    aria-expanded={selectorOpen}
                >

                    <span>
                        CHART
                    </span>

                </button>


                {/* ======================================
                    VERTICAL RAIL
                ====================================== */}

                <div
                    className="home-intelligence-card-line"
                />

            </div>


            {/* ==========================================
                MAIN CONTENT
            ========================================== */}

            <div className="home-intelligence-card-main">

                {/* ======================================
                    HEADER
                ====================================== */}

                <div className="home-intelligence-card-header">

                    <div className="home-intelligence-card-heading">

                        <div className="home-intelligence-card-title">

                            {subject.title}

                        </div>

                        <div className="home-intelligence-card-description">

                            {subject.description || ""}

                        </div>

                    </div>

                </div>


                {/* ======================================
                    CHART AREA
                ====================================== */}

                <div className="home-intelligence-card-chart">

                    {
                        loading
                        ?
                        (
                            <div className="home-intelligence-chart-message">
                                Loading intelligence...
                            </div>
                        )
                        :
                        error
                        ?
                        (
                            <div className="home-intelligence-chart-message">
                                Unable to load intelligence.
                            </div>
                        )
                        :
                        data
                        ?
                        (
                            <HomeIntelligenceChart
                                data={data}
                                chartType={chartType}
                                color={subject.color}
                            />
                        )
                        :
                        (
                            <div className="home-intelligence-chart-message">
                                No intelligence data available.
                            </div>
                        )
                    }

                </div>

            </div>


            {/* ==========================================
                SLIDING CHART SELECTOR
            ========================================== */}

            <div
                className={
                    "home-intelligence-card-selector"
                    +
                    (
                        selectorOpen
                            ?
                            " open"
                            :
                            ""
                    )
                }

                aria-hidden={!selectorOpen}
            >

                {/* ======================================
                    SELECTOR HEADER
                ====================================== */}

                <div className="home-intelligence-card-selector-header">

                    <span>
                        CHART TYPE
                    </span>


                    <button
                        type="button"

                        className="home-intelligence-card-selector-close"

                        onClick={
                            function()
                            {
                                setSelectorOpen(false);
                            }
                        }

                        aria-label="Close chart selector"

                        tabIndex={selectorOpen ? 0 : -1}
                    >

                        <span aria-hidden="true">
                            ×
                        </span>

                    </button>

                </div>


                {/* ======================================
                    CHART TYPE OPTIONS
                ====================================== */}

                <div className="home-intelligence-card-selector-list">

                    {
                        chartTypes.map(
                            function(type)
                            {
                                const selected =
                                    chartType === type;

                                return (

                                    <button
                                        type="button"

                                        key={type}

                                        className={
                                            "home-intelligence-card-option"
                                            +
                                            (
                                                selected
                                                    ?
                                                    " selected"
                                                    :
                                                    ""
                                            )
                                        }

                                        onClick={
                                            function()
                                            {
                                                selectChartType(type);
                                            }
                                        }

                                        tabIndex={
                                            selectorOpen ? 0 : -1
                                        }
                                    >

                                        <span className="home-intelligence-card-option-name">

                                            {type}

                                        </span>


                                        {
                                            selected
                                            &&
                                            <span
                                                className="home-intelligence-card-option-check"
                                                aria-hidden="true"
                                            >
                                                ✓
                                            </span>
                                        }

                                    </button>

                                );

                            }
                        )
                    }

                </div>

            </div>

        </div>

    );

}
